import { useState, useEffect } from 'react';
import { useNotify } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Avatar,
  Chip,
  Button,
} from '@mui/material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PeopleIcon from '@mui/icons-material/People';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import PublicIcon from '@mui/icons-material/Public';
import HistoryIcon from '@mui/icons-material/History';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import SettingsIcon from '@mui/icons-material/Settings';

const API_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:3000';

interface DashboardStats {
  balance: number | null;
  userCount: number;
  serviceCount: number;
  countryCount: number;
  completedCount: number;
  cancelledCount: number;
  systemStatus: {
    heroSmsApi: boolean;
    database: boolean;
  };
}

export const Dashboard = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const notify = useNotify();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/api/admin/sms/dashboard/stats`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await response.json();
        setStats(data);
      } catch (error) {
        notify('Failed to load dashboard data', { type: 'error' });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [notify]);

  return (
    <Box>
      {/* Welcome Card */}
      <Card
        sx={{
          background: 'linear-gradient(118deg, #7367f0, rgba(115, 103, 240, 0.7))',
          mb: 3,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h5" sx={{ color: 'white', fontWeight: 600, mb: 1 }}>
                Welcome!
              </Typography>
              <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.8)', mb: 2 }}>
                SMS Hero Admin Panel
              </Typography>
              <Button
                variant="contained"
                size="small"
                onClick={() => navigate('/admin/sms/activations')}
                sx={{
                  backgroundColor: 'white',
                  color: '#7367f0',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.9)' },
                  boxShadow: 'none',
                }}
              >
                View History
              </Button>
            </Box>
            <Box
              sx={{
                width: 150,
                height: 150,
                borderRadius: '50%',
                backgroundColor: 'rgba(255,255,255,0.1)',
                display: { xs: 'none', sm: 'flex' },
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography variant="h2" sx={{ color: 'white' }}>
                📊
              </Typography>
            </Box>
          </Box>
        </CardContent>
        <Box
          sx={{
            position: 'absolute',
            right: -40,
            top: -40,
            width: 200,
            height: 200,
            borderRadius: '50%',
            backgroundColor: 'rgba(255,255,255,0.08)',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            right: 60,
            bottom: -60,
            width: 150,
            height: 150,
            borderRadius: '50%',
            backgroundColor: 'rgba(255,255,255,0.05)',
          }}
        />
      </Card>

      {/* Stats Grid */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(4, 1fr)' },
          gap: 2,
          mb: 3,
        }}
      >
        {/* Balance Card */}
        <Card>
          <CardContent sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography variant="body2" sx={{ color: '#676d7d', mb: 0.5 }}>
                  Balance
                </Typography>
                {loading ? (
                  <CircularProgress size={24} sx={{ color: '#7367f0' }} />
                ) : (
                  <Typography variant="h5" sx={{ color: '#d0d2d6', fontWeight: 600 }}>
                    ${stats?.balance?.toFixed(2) || '0.00'}
                  </Typography>
                )}
              </Box>
              <Avatar
                sx={{
                  width: 44,
                  height: 44,
                  backgroundColor: 'rgba(115, 103, 240, 0.12)',
                }}
              >
                <AccountBalanceWalletIcon sx={{ color: '#7367f0', fontSize: 24 }} />
              </Avatar>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1.5 }}>
              <Typography variant="caption" sx={{ color: '#676d7d' }}>
                HeroSMS balance
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Users Card */}
        <Card>
          <CardContent sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography variant="body2" sx={{ color: '#676d7d', mb: 0.5 }}>
                  Users
                </Typography>
                {loading ? (
                  <CircularProgress size={24} sx={{ color: '#00cfe8' }} />
                ) : (
                  <Typography variant="h5" sx={{ color: '#d0d2d6', fontWeight: 600 }}>
                    {stats?.userCount ?? '--'}
                  </Typography>
                )}
              </Box>
              <Avatar
                sx={{
                  width: 44,
                  height: 44,
                  backgroundColor: 'rgba(0, 207, 232, 0.12)',
                }}
              >
                <PeopleIcon sx={{ color: '#00cfe8', fontSize: 24 }} />
              </Avatar>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1.5 }}>
              <Typography variant="caption" sx={{ color: '#676d7d' }}>
                Registered users
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Services Card */}
        <Card>
          <CardContent sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography variant="body2" sx={{ color: '#676d7d', mb: 0.5 }}>
                  Services
                </Typography>
                {loading ? (
                  <CircularProgress size={24} sx={{ color: '#ff9f43' }} />
                ) : (
                  <Typography variant="h5" sx={{ color: '#d0d2d6', fontWeight: 600 }}>
                    {stats?.serviceCount ?? '--'}
                  </Typography>
                )}
              </Box>
              <Avatar
                sx={{
                  width: 44,
                  height: 44,
                  backgroundColor: 'rgba(255, 159, 67, 0.12)',
                }}
              >
                <PhoneAndroidIcon sx={{ color: '#ff9f43', fontSize: 24 }} />
              </Avatar>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1.5 }}>
              <Typography variant="caption" sx={{ color: '#676d7d' }}>
                Available services
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Countries Card */}
        <Card>
          <CardContent sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography variant="body2" sx={{ color: '#676d7d', mb: 0.5 }}>
                  Countries
                </Typography>
                {loading ? (
                  <CircularProgress size={24} sx={{ color: '#ea5455' }} />
                ) : (
                  <Typography variant="h5" sx={{ color: '#d0d2d6', fontWeight: 600 }}>
                    {stats?.countryCount ?? '--'}
                  </Typography>
                )}
              </Box>
              <Avatar
                sx={{
                  width: 44,
                  height: 44,
                  backgroundColor: 'rgba(234, 84, 85, 0.12)',
                }}
              >
                <PublicIcon sx={{ color: '#ea5455', fontSize: 24 }} />
              </Avatar>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1.5 }}>
              <Typography variant="caption" sx={{ color: '#676d7d' }}>
                Supported countries
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Two Column Layout */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
          gap: 2,
          mb: 3,
        }}
      >
        {/* Activation Stats Card */}
        <Card>
          <CardContent sx={{ p: 0 }}>
            <Box sx={{ p: 2.5, borderBottom: '1px solid #404656' }}>
              <Typography variant="h6" sx={{ color: '#d0d2d6', fontWeight: 600 }}>
                Activation Stats
              </Typography>
            </Box>
            <Box sx={{ p: 2.5 }}>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress size={28} sx={{ color: '#7367f0' }} />
                </Box>
              ) : (
                <>
                  {/* Completed */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      py: 2,
                      borderBottom: '1px solid #404656',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar
                        sx={{
                          width: 40,
                          height: 40,
                          backgroundColor: 'rgba(40, 199, 111, 0.12)',
                        }}
                      >
                        <CheckCircleIcon sx={{ color: '#28c76f', fontSize: 22 }} />
                      </Avatar>
                      <Typography variant="body2" sx={{ color: '#d0d2d6', fontWeight: 500 }}>
                        Completed
                      </Typography>
                    </Box>
                    <Typography variant="h6" sx={{ color: '#28c76f', fontWeight: 600 }}>
                      {stats?.completedCount ?? 0}
                    </Typography>
                  </Box>

                  {/* Cancelled */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      py: 2,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar
                        sx={{
                          width: 40,
                          height: 40,
                          backgroundColor: 'rgba(234, 84, 85, 0.12)',
                        }}
                      >
                        <CancelIcon sx={{ color: '#ea5455', fontSize: 22 }} />
                      </Avatar>
                      <Typography variant="body2" sx={{ color: '#d0d2d6', fontWeight: 500 }}>
                        Cancelled
                      </Typography>
                    </Box>
                    <Typography variant="h6" sx={{ color: '#ea5455', fontWeight: 600 }}>
                      {stats?.cancelledCount ?? 0}
                    </Typography>
                  </Box>
                </>
              )}
            </Box>
          </CardContent>
        </Card>

        {/* System Status Card */}
        <Card>
          <CardContent sx={{ p: 0 }}>
            <Box sx={{ p: 2.5, borderBottom: '1px solid #404656' }}>
              <Typography variant="h6" sx={{ color: '#d0d2d6', fontWeight: 600 }}>
                System Status
              </Typography>
            </Box>
            <Box sx={{ p: 2.5 }}>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress size={28} sx={{ color: '#7367f0' }} />
                </Box>
              ) : (
                <>
                  {/* HeroSMS API */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      py: 2,
                      borderBottom: '1px solid #404656',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar
                        sx={{
                          width: 40,
                          height: 40,
                          backgroundColor: stats?.systemStatus.heroSmsApi
                            ? 'rgba(40, 199, 111, 0.12)'
                            : 'rgba(234, 84, 85, 0.12)',
                        }}
                      >
                        {stats?.systemStatus.heroSmsApi ? (
                          <CheckCircleIcon sx={{ color: '#28c76f', fontSize: 22 }} />
                        ) : (
                          <CancelIcon sx={{ color: '#ea5455', fontSize: 22 }} />
                        )}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" sx={{ color: '#d0d2d6', fontWeight: 500 }}>
                          HeroSMS API
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#676d7d' }}>
                          SMS provider connection
                        </Typography>
                      </Box>
                    </Box>
                    <Chip
                      label={stats?.systemStatus.heroSmsApi ? 'Online' : 'Offline'}
                      size="small"
                      sx={{
                        backgroundColor: stats?.systemStatus.heroSmsApi
                          ? 'rgba(40, 199, 111, 0.12)'
                          : 'rgba(234, 84, 85, 0.12)',
                        color: stats?.systemStatus.heroSmsApi ? '#28c76f' : '#ea5455',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                      }}
                    />
                  </Box>

                  {/* Database */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      py: 2,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar
                        sx={{
                          width: 40,
                          height: 40,
                          backgroundColor: stats?.systemStatus.database
                            ? 'rgba(40, 199, 111, 0.12)'
                            : 'rgba(234, 84, 85, 0.12)',
                        }}
                      >
                        {stats?.systemStatus.database ? (
                          <CheckCircleIcon sx={{ color: '#28c76f', fontSize: 22 }} />
                        ) : (
                          <CancelIcon sx={{ color: '#ea5455', fontSize: 22 }} />
                        )}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" sx={{ color: '#d0d2d6', fontWeight: 500 }}>
                          Database
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#676d7d' }}>
                          MongoDB connection
                        </Typography>
                      </Box>
                    </Box>
                    <Chip
                      label={stats?.systemStatus.database ? 'Online' : 'Offline'}
                      size="small"
                      sx={{
                        backgroundColor: stats?.systemStatus.database
                          ? 'rgba(40, 199, 111, 0.12)'
                          : 'rgba(234, 84, 85, 0.12)',
                        color: stats?.systemStatus.database ? '#28c76f' : '#ea5455',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                      }}
                    />
                  </Box>
                </>
              )}
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Quick Actions */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
          gap: 2,
        }}
      >
        <Card
          onClick={() => navigate('/users')}
          sx={{
            background: 'linear-gradient(135deg, rgba(234, 84, 85, 0.12) 0%, rgba(234, 84, 85, 0.04) 100%)',
            cursor: 'pointer',
            transition: 'transform 0.2s',
            '&:hover': { transform: 'translateY(-4px)' },
          }}
        >
          <CardContent sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ backgroundColor: '#ea5455', width: 48, height: 48 }}>
              <PeopleIcon sx={{ color: 'white' }} />
            </Avatar>
            <Box>
              <Typography variant="subtitle1" sx={{ color: '#d0d2d6', fontWeight: 600 }}>
                Manage Users
              </Typography>
              <Typography variant="body2" sx={{ color: '#676d7d' }}>
                View user list
              </Typography>
            </Box>
          </CardContent>
        </Card>

        <Card
          onClick={() => navigate('/admin/sms/activations')}
          sx={{
            background: 'linear-gradient(135deg, rgba(255, 159, 67, 0.12) 0%, rgba(255, 159, 67, 0.04) 100%)',
            cursor: 'pointer',
            transition: 'transform 0.2s',
            '&:hover': { transform: 'translateY(-4px)' },
          }}
        >
          <CardContent sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ backgroundColor: '#ff9f43', width: 48, height: 48 }}>
              <HistoryIcon sx={{ color: 'white' }} />
            </Avatar>
            <Box>
              <Typography variant="subtitle1" sx={{ color: '#d0d2d6', fontWeight: 600 }}>
                Activations
              </Typography>
              <Typography variant="body2" sx={{ color: '#676d7d' }}>
                View activation history
              </Typography>
            </Box>
          </CardContent>
        </Card>

        <Card
          onClick={() => navigate('/admin/sms/services')}
          sx={{
            background: 'linear-gradient(135deg, rgba(40, 199, 111, 0.12) 0%, rgba(40, 199, 111, 0.04) 100%)',
            cursor: 'pointer',
            transition: 'transform 0.2s',
            '&:hover': { transform: 'translateY(-4px)' },
          }}
        >
          <CardContent sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ backgroundColor: '#28c76f', width: 48, height: 48 }}>
              <SettingsIcon sx={{ color: 'white' }} />
            </Avatar>
            <Box>
              <Typography variant="subtitle1" sx={{ color: '#d0d2d6', fontWeight: 600 }}>
                Services
              </Typography>
              <Typography variant="body2" sx={{ color: '#676d7d' }}>
                View available services
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};
